import { useState, type FormEvent } from "react";
import { EVENT_TYPES, type EventType } from "@mmm/contracts";
import { EVENT_TYPE_COLORS, EVENT_TYPE_LABEL_KEYS } from "../../constants.js";
import { Button, Icon, Input, Pill, useT } from "@mmm/ui";
interface EventDraft {
  title: string;
  type: EventType;
  startDate: string;
  endDate: string;
}

export interface EventFormValues {
  title: string;
  type: EventType;
  startDate: string;
  endDate: string | null;
}

interface SelectedEventLike {
  title: string;
  type: EventType;
  startDate: string;
  endDate: string | null;
}

const EMPTY: EventDraft = { title: "", type: "assignment", startDate: "", endDate: "" };

interface EventFormProps {
  selectedEvent?: SelectedEventLike | null;
  onSave: (values: EventFormValues) => Promise<unknown>;
  onDelete?: () => void;
  onClear: () => void;
}

export function EventForm({ selectedEvent, onSave, onDelete, onClear }: EventFormProps) {
  const t = useT();
  const [draft, setDraft] = useState<EventDraft>(() =>
    selectedEvent
      ? {
          title: selectedEvent.title,
          type: selectedEvent.type,
          startDate: selectedEvent.startDate,
          endDate: selectedEvent.endDate || "",
        }
      : EMPTY
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.title.trim() || !draft.startDate) return;
    // The <form>'s onSubmit invokes this without awaiting it — a failed save
    // is already toasted by commands.ts's run(); catch it here so the
    // rejection doesn't surface as unhandled, and only clear the draft once
    // the save actually succeeds (a failure should leave what was typed).
    try {
      await onSave({
        title: draft.title.trim(),
        type: draft.type,
        startDate: draft.startDate,
        endDate: draft.endDate || null,
      });
      if (!selectedEvent) setDraft(EMPTY);
    } catch {
      // Nothing left to do.
    }
  };

  const submitLabel = selectedEvent ? t("events.saveEvent") : t("events.addEvent");

  return (
    <form onSubmit={handleSubmit} className="mmm-entry-form mmm-event-entry-form">
      <span className="mmm-entry-form-icon">
        <Icon name="add" />
      </span>
      <div className="mmm-event-entry-content">
        <div className="mmm-event-type-picker">
          {EVENT_TYPES.map((type) => {
            const colors = EVENT_TYPE_COLORS[type];
            const active = draft.type === type;
            return (
              <Pill
                key={type}
                active={active}
                color={colors.text}
                activeBackground={colors.bg}
                onClick={() => setDraft((d) => ({ ...d, type }))}
              >
                {t(EVENT_TYPE_LABEL_KEYS[type])}
              </Pill>
            );
          })}
        </div>
        <div className="mmm-event-entry-main">
          <Input
            placeholder={t("events.titlePlaceholder")}
            value={draft.title}
            onChange={(event) => setDraft((d) => ({ ...d, title: event.target.value }))}
          />
          <Input
            type="date"
            aria-label={t("events.date")}
            value={draft.startDate}
            onChange={(event) => setDraft((d) => ({ ...d, startDate: event.target.value }))}
          />
          <Button type="submit" variant="primary" disabled={!draft.title.trim() || !draft.startDate}>
            {submitLabel}
          </Button>
          {selectedEvent && (
            <Button variant="danger" onClick={onDelete}>
              {t("common.delete")}
            </Button>
          )}
          <Button variant="secondary" onClick={onClear}>
            {t("common.clear")}
          </Button>
        </div>
      </div>
    </form>
  );
}
